import React, { useCallback, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Form, Modal, Button, Spinner } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import InvoiceItemBootstrap from './InvoiceItemBootstrap';
import axios from '../../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../../utils/ApiUtils';
import InvoiceTimeImportModal from './InvoiceTimeImportModal';

const InvoiceItemListBootstrap = ({
  items = [],
  onItemsChange,
  availableArticles = [],
  roundTotal = false,
  onRoundingChange,
  defaultVatRate = '25%',
  currency = 'SEK',
  vatTreatment = 'taxable',
  companyId,
  customerId,
}) => {
  // For non-taxable invoices (RC, exempt, out-of-scope) the seller does
  // not charge output VAT — line tax_rate is preserved on the row but
  // ignored in display + totals + verifikat (verifikat skip is wired in
  // backend VerificationService).
  const isTaxable = vatTreatment === 'taxable';
  const [showTimeImport, setShowTimeImport] = useState(false);
  const [timeImportPreview, setTimeImportPreview] = useState(null);
  const [timeImportLoading, setTimeImportLoading] = useState(false);
  const [timeImportError, setTimeImportError] = useState(null);
  const [importedEntryIds, setImportedEntryIds] = useState(new Set()); // Prevent double-import in this session
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItemData, setNewItemData] = useState({
    productcode: '',
    description: '',
    unitprice: 0,
    quantity: 1,
    unit: 'Piece',
    tax_rate: `${defaultVatRate}%`,
    type: 'Goods',
    article_id: null
  });

  // Reset new item data
  const resetNewItemData = useCallback(() => {
    setNewItemData({
      productcode: '',
      description: '',
      unitprice: 0,
      quantity: 1,
      unit: 'Piece',
      tax_rate: `${defaultVatRate}%`,
      type: 'Goods',
      article_id: null
    });
  }, [defaultVatRate]);

  // Show add item modal
  const handleShowAddModal = useCallback(() => {
    resetNewItemData();
    setShowAddModal(true);
  }, [resetNewItemData]);

  // Hide add item modal
  const handleHideAddModal = useCallback(() => {
    setShowAddModal(false);
    resetNewItemData();
  }, [resetNewItemData]);

  // Format currency
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }, [currency]);

  // Format quantity for display (removes trailing zeros)
  // 1.000 → "1", 2.500 → "2.5", 1.250 → "1.25"
  const formatQuantity = useCallback((qty) => {
    if (!qty) return '1';
    const num = parseFloat(qty);
    return num.toFixed(3).replace(/\.?0+$/, '');
  }, []);

  // Format article display
  const getArticleLabel = useCallback((article) => {
    return `${article.productcode || ''} - ${article.description || ''} (${formatCurrency(article.unitprice || 0)})`;
  }, [formatCurrency]);

  // Filter articles by search term
  const filterArticles = useCallback((inputValue) => {
    const searchTerm = inputValue.toLowerCase();
    return availableArticles.filter(article =>
      article.productcode?.toLowerCase().includes(searchTerm) ||
      article.description?.toLowerCase().includes(searchTerm)
    );
  }, [availableArticles]);

  // Load article options for the async select
  const loadArticleOptions = useCallback((inputValue) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const filteredArticles = filterArticles(inputValue);
        const options = filteredArticles.slice(0, 50).map(article => ({
          value: article.id,
          label: getArticleLabel(article),
          article: article
        }));
        resolve(options);
      }, 300);
    });
  }, [filterArticles, getArticleLabel]);

  // Handle article selection in modal
  const handleArticleSelect = useCallback((selectedOption) => {
    if (selectedOption && selectedOption.article) {
      const article = selectedOption.article;
      setNewItemData({
        productcode: article.productcode || '',
        description: article.description || '',
        unitprice: parseFloat(article.unitprice || 0),
        quantity: 1,
        unit: article.unit || 'Piece',
        tax_rate: article.tax_rate || `${defaultVatRate}%`,
        type: article.type || 'Product',
        article_id: article.id
      });
    } else {
      // Clear article data but keep manual entries
      setNewItemData(prev => ({
        ...prev,
        article_id: null
      }));
    }
  }, [defaultVatRate]);

  // Handle manual field changes in modal
  const handleNewItemFieldChange = useCallback((field, value) => {
    setNewItemData(prev => ({
      ...prev,
      [field]: value,
      // Clear article_id if manually editing (unless it's quantity change)
      ...(field !== 'quantity' ? { article_id: null } : {})
    }));
  }, []);

  // Add the configured item to the list
  const handleAddConfiguredItem = useCallback(() => {
    // Validate required fields
    if (!newItemData.description?.trim()) {
      alert('Beskrivning krävs');
      return;
    }

    if (newItemData.unitprice <= 0) {
      alert('Priset måste vara större än 0');
      return;
    }

    // Create new item with UUID
    const newItem = {
      id: uuidv4(),
      ...newItemData,
      isNew: true
    };

    // Add to items list
    onItemsChange([...items, newItem]);

    // Close modal and reset
    handleHideAddModal();
  }, [newItemData, items, onItemsChange, handleHideAddModal]);

  // Quick add from article (bypass modal for known articles)
  const handleQuickAddArticle = useCallback((article) => {
    const newItem = {
      id: uuidv4(),
      productcode: article.productcode || '',
      description: article.description || '',
      unitprice: parseFloat(article.unitprice || 0),
      quantity: 1,
      unit: article.unit || 'Piece',
      tax_rate: article.tax_rate || `${defaultVatRate}%`,
      type: article.type || 'Product',
      article_id: article.id,
      isNew: true
    };

    console.log('Quick adding article:', newItem);
    onItemsChange([...items, newItem]);
  }, [items, onItemsChange, defaultVatRate]);

  // Update existing item
  const handleUpdateItem = useCallback((index, updatedItem) => {
    const newItems = [...items];
    newItems[index] = { ...updatedItem, isModified: true };
    onItemsChange(newItems);
  }, [items, onItemsChange]);

  // Remove item — also release any time-import entry IDs back into the
  // session pool so the user can re-import the same range after undo.
  const handleRemoveItem = useCallback((index) => {
    const removed = items[index];
    if (removed?._timeImportEntryIds?.length) {
      setImportedEntryIds(prev => {
        const next = new Set(prev);
        removed._timeImportEntryIds.forEach(eid => next.delete(eid));
        return next;
      });
    }
    const newItems = items.filter((_, idx) => idx !== index);
    onItemsChange(newItems);
  }, [items, onItemsChange]);

  // Select predefined article for existing item
  const handleSelectArticle = useCallback((itemId, articleId) => {
    if (!articleId) return;

    const selectedArticle = availableArticles.find(article => article.id === articleId);
    if (!selectedArticle) return;

    const itemIndex = items.findIndex(item => item.id === itemId);
    if (itemIndex !== -1) {
      const updatedItem = {
        ...items[itemIndex],
        article_id: selectedArticle.id,
        productcode: selectedArticle.productcode || '',
        description: selectedArticle.description || '',
        unitprice: parseFloat(selectedArticle.unitprice || 0),
        unit: selectedArticle.unit || 'Piece',
        tax_rate: selectedArticle.tax_rate || `${defaultVatRate}%`,
        type: selectedArticle.type || 'Product',
        isModified: true
      };

      handleUpdateItem(itemIndex, updatedItem);
    }
  }, [availableArticles, items, defaultVatRate, handleUpdateItem]);

  // Calculate totals with memoization
  const calculations = useMemo(() => {
    const taxGroups = {};
    let subtotal = 0;

    items.forEach(item => {
      const quantity = parseFloat(item.quantity) || 0;
      const unitPrice = parseFloat(item.unitprice) || 0;
      const taxRate = parseFloat(item.tax_rate?.replace('%', '')) || 0;

      const lineTotal = quantity * unitPrice;

      // Only accumulate tax groups when the invoice is taxable. For RC /
      // exempt / out-of-scope invoices the seller posts no output VAT, so
      // the totals card must not show moms rows or sum moms into the total.
      if (isTaxable) {
        if (!taxGroups[taxRate]) {
          taxGroups[taxRate] = { subtotal: 0, tax: 0 };
        }
        taxGroups[taxRate].subtotal += lineTotal;
        taxGroups[taxRate].tax += lineTotal * (taxRate / 100);
      }

      subtotal += lineTotal;
    });

    const totalTax = Object.values(taxGroups).reduce((sum, group) => sum + group.tax, 0);
    const grandTotal = subtotal + totalTax;

    let roundingAmount = 0;
    let finalTotal = grandTotal;

    if (roundTotal && grandTotal > 0) {
      finalTotal = Math.round(grandTotal);
      roundingAmount = finalTotal - grandTotal;
    }

    return {
      taxGroups,
      subtotal,
      totalTax,
      grandTotal,
      roundingAmount,
      finalTotal
    };
  }, [items, roundTotal, isTaxable]);

  // Handle rounding toggle
  const handleRoundingToggle = useCallback((checked) => {
    if (onRoundingChange) {
      onRoundingChange(checked);
    }
  }, [onRoundingChange]);

  return (
    <>
      <style>{`
        .empty-items-state {
          text-align: center;
          padding: 3rem 1.5rem 2.5rem;
          margin-bottom: 1rem;
        }

        .empty-items-icon {
          width: 56px;
          height: 56px;
          margin: 0 auto 1.25rem;
          background: var(--rgba-primary-1);
          border: 1px solid var(--primary);
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary);
          font-size: 1.5rem;
        }

        .empty-items-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 0.5rem 0;
        }

        .empty-items-description {
          color: #64748b;
          font-size: 0.9375rem;
          line-height: 1.5;
          max-width: 380px;
          margin: 0 auto 1.5rem;
        }

        .empty-state-cta {
          padding: 0.75rem 1.5rem;
          border-radius: 10px;
          font-weight: 500;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .empty-state-link,
        .items-footer-link {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          color: var(--primary);
          background: transparent;
          border: none;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          font-weight: 500;
          text-decoration: none;
          transition: color 0.15s ease;
        }

        .empty-state-link:hover,
        .items-footer-link:hover {
          color: var(--primary-hover);
          text-decoration: underline;
        }

        .empty-state-link {
          margin-top: 0.25rem;
        }

        .empty-state-quick-add {
          margin-top: 2.5rem;
          max-width: 540px;
          margin-left: auto;
          margin-right: auto;
        }

        .quick-add-divider {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #94a3b8;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          margin-bottom: 1rem;
        }

        .quick-add-divider::before,
        .quick-add-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: #e2e8f0;
        }

        .article-quick-add {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          justify-content: center;
        }

        .article-quick-add .btn {
          font-size: 0.8125rem;
          padding: 0.375rem 0.75rem;
          border-radius: 999px;
        }

        .invoice-items-footer {
          margin-top: 1.5rem;
          padding-top: 1.25rem;
          border-top: 1px dashed #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .quote-summary {
          margin-top: 2rem;
          padding-top: 1rem;
          border-top: 1px solid var(--bs-light);
        }

        .summary-card {
          background: var(--bs-light);
          padding: 1.5rem;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
          padding: 0.25rem 0;
        }

        .summary-row.total {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 2px solid var(--bs-light);
          font-weight: bold;
          font-size: 1.1em;
        }
      `}</style>

      <div className="mb-4">
        {companyId && customerId && (
          <InvoiceTimeImportModal
            show={showTimeImport}
            previewData={timeImportPreview}
            loading={timeImportLoading}
            error={timeImportError}
            importing={false}
            onClose={() => {
              setShowTimeImport(false);
              setTimeImportPreview(null);
              setTimeImportError(null);
            }}
            onLoadPreview={async ({ fromDate, toDate, groupBy }) => {
              setTimeImportLoading(true);
              setTimeImportError(null);
              try {
                const res = await axios.get(
                  getApiUrl(`${companyId}/time-reporting/billable-entries`),
                  { params: { customerId, fromDate, toDate, groupBy }, withCredentials: true },
                );
                setTimeImportPreview(res.data);
              } catch (err) {
                setTimeImportError(err.response?.data?.message || 'Kunde inte ladda tidrapportdata');
              } finally {
                setTimeImportLoading(false);
              }
            }}
            onConfirmImport={({ selectedGroups }) => {
              if (!timeImportPreview?.groups) return;
              const selectedKeys = new Set(selectedGroups.map(g => g.groupKey));
              const descOverrides = Object.fromEntries(
                selectedGroups.filter(g => g.description).map(g => [g.groupKey, g.description])
              );

              // Filter out groups whose entries are already imported in this session
              const groupsToImport = timeImportPreview.groups
                .filter(g => selectedKeys.has(g.groupKey))
                .map(g => ({
                  ...g,
                  entryIds: (g.entryIds || []).filter(eid => !importedEntryIds.has(eid)),
                }))
                .filter(g => g.entryIds.length > 0);

              if (groupsToImport.length === 0) {
                setTimeImportError('Alla valda poster är redan importerade på denna faktura.');
                return;
              }

              const newlyImported = new Set(importedEntryIds);
              groupsToImport.forEach(g => g.entryIds.forEach(eid => newlyImported.add(eid)));
              setImportedEntryIds(newlyImported);

              const newItems = groupsToImport.map(group => ({
                id: uuidv4(),
                article_id: group.articleId || '',
                productcode: group.articleProductcode || '',
                description: descOverrides[group.groupKey] || group.label,
                _fromTimeImport: true,
                _timeImportEntryIds: group.entryIds,
                quantity: group.totalHours,
                unitprice: group.billingRate,
                tax_rate: group.taxRate || '25%',
                type: 'Service',
                unit: 'Hour',
              }));

              onItemsChange([...items, ...newItems]);
              setShowTimeImport(false);
              setTimeImportPreview(null);
            }}
          />
        )}

        {items.length === 0 ? (
          <div className="empty-items-state">
            <div className="empty-items-icon">
              <i className="bi bi-basket"></i>
            </div>
            <h3 className="empty-items-title">Inga artiklar tillagda</h3>
            <p className="empty-items-description">
              Lägg till produkter eller tjänster som ska faktureras
            </p>

            <button
              type="button"
              className="btn btn-primary empty-state-cta"
              onClick={handleShowAddModal}
            >
              <i className="bi bi-plus me-2"></i>
              Lägg till artikel eller produkt
            </button>

            {companyId && customerId && (
              <div>
                <button
                  type="button"
                  className="empty-state-link"
                  onClick={() => {
                    setTimeImportPreview(null);
                    setTimeImportError(null);
                    setShowTimeImport(true);
                  }}
                >
                  <i className="bi bi-clock"></i>
                  Importera från tidrapport
                  <i className="bi bi-arrow-up-right" style={{ fontSize: '0.75em' }}></i>
                </button>
              </div>
            )}

            {availableArticles.length > 0 && (
              <div className="empty-state-quick-add">
                <div className="quick-add-divider">
                  <span>Snabbtillägg</span>
                </div>
                <div className="article-quick-add">
                  {availableArticles.slice(0, 6).map(article => (
                    <button
                      key={article.id}
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => handleQuickAddArticle(article)}
                      title={article.description}
                    >
                      {article.productcode || article.description?.substring(0, 20) + '...'}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {items.map((item, index) => (
              <div key={item.id} style={{ position: 'relative' }}>
                {item._fromTimeImport && (
                  <span
                    title="Importerad från tidrapport"
                    style={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '2px 6px',
                      borderRadius: 99,
                      color: '#0369a1',
                      backgroundColor: 'rgba(14, 165, 233, 0.1)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 3,
                      zIndex: 1,
                      pointerEvents: 'none',
                    }}
                  >
                    🕐 Tid
                  </span>
                )}
                <InvoiceItemBootstrap
                  item={item}
                  index={index}
                  isLast={index === items.length - 1}
                  onUpdate={(updatedItem) => handleUpdateItem(index, { ...updatedItem, _fromTimeImport: item._fromTimeImport, _timeImportEntryIds: item._timeImportEntryIds })}
                  onRemove={() => handleRemoveItem(index)}
                  availableItems={availableArticles}
                  onSelectArticle={handleSelectArticle}
                  defaultVatRate={defaultVatRate}
                  currency={currency}
                  isTaxable={isTaxable}
                />
              </div>
            ))}

            <div className="invoice-items-footer">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handleShowAddModal}
              >
                <i className="bi bi-plus me-2"></i>
                Lägg till fler artiklar
              </button>
              {companyId && customerId && (
                <button
                  type="button"
                  className="items-footer-link"
                  onClick={() => {
                    setTimeImportPreview(null);
                    setTimeImportError(null);
                    setShowTimeImport(true);
                  }}
                >
                  <i className="bi bi-clock"></i>
                  Importera från tidrapport
                </button>
              )}
            </div>
          </>
        )}

        {/* Add Item Modal */}
        <Modal show={showAddModal} onHide={handleHideAddModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Lägg till artikel</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {availableArticles.length > 0 && (
              <div className="mb-4">
                <label className="form-label">Välj från artikelbibliotek</label>
                <AsyncSelect
                  cacheOptions
                  defaultOptions={availableArticles.slice(0, 10).map(article => ({
                    value: article.id,
                    label: getArticleLabel(article),
                    article: article
                  }))}
                  value={newItemData.article_id ? {
                    value: newItemData.article_id,
                    label: getArticleLabel(
                      availableArticles.find(a => a.id === newItemData.article_id) || {}
                    )
                  } : null}
                  loadOptions={loadArticleOptions}
                  onChange={handleArticleSelect}
                  isClearable
                  placeholder="Sök efter artikelkod eller beskrivning..."
                  noOptionsMessage={() => "Inga artiklar hittades"}
                  loadingMessage={() => "Söker artiklar..."}
                />
                <div className="form-text">
                  {newItemData.article_id ? (
                    <span className="text-success">
                      <i className="bi bi-check-circle me-1"></i>
                      Artikel vald från bibliotek
                    </span>
                  ) : (
                    <span className="text-muted">
                      Eller skapa en anpassad artikel nedan
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">Artikelkod</label>
                <input
                  type="text"
                  className="form-control"
                  value={newItemData.productcode}
                  onChange={(e) => handleNewItemFieldChange('productcode', e.target.value)}
                  placeholder="T.ex. ART-001"
                />
              </div>
              <div className="col-md-8">
                <label className="form-label">Beskrivning *</label>
                <input
                  type="text"
                  className="form-control"
                  value={newItemData.description}
                  onChange={(e) => handleNewItemFieldChange('description', e.target.value)}
                  placeholder="Beskrivning av artikeln"
                  required
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-3">
                <label className="form-label">Antal</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  step="0.5"
                  value={newItemData.quantity}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty string for better UX while typing
                    if (value === '') {
                      handleNewItemFieldChange('quantity', '');
                      return;
                    }
                    const parsed = parseFloat(value);
                    if (!isNaN(parsed) && parsed > 0) {
                      handleNewItemFieldChange('quantity', parsed);
                    }
                  }}
                  onBlur={(e) => {
                    // On blur, ensure we have a valid number
                    const value = parseFloat(e.target.value);
                    if (isNaN(value) || value < 0.001) {
                      handleNewItemFieldChange('quantity', 1);
                    }
                  }}
                  placeholder="t.ex. 2.5"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Enhet</label>
                <select
                  className="form-select"
                  value={newItemData.unit}
                  onChange={(e) => handleNewItemFieldChange('unit', e.target.value)}
                >
                  <option value="Piece">st (Piece)</option>
                  <option value="Hour">tim (Hour)</option>
                  <option value="Day">dag (Day)</option>
                  <option value="Month">mån (Month)</option>
                  <option value="Package">frp (Package)</option>
                  <option value="Kg">kg (Kg)</option>
                  <option value="Meter">m (Meter)</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Pris *</label>
                <input
                  type="number"
                  className="form-control"
                  min="0"
                  step="0.01"
                  value={newItemData.unitprice}
                  onChange={(e) => handleNewItemFieldChange('unitprice', parseFloat(e.target.value) || 0)}
                  required
                />
              </div>
              {/* Moms field hidden for non-taxable invoices (RC / exempt /
                  out-of-scope) — line tax_rate is irrelevant when the seller
                  posts no output VAT. The line still defaults to '25%' on
                  the row but is ignored everywhere downstream. */}
              {isTaxable && (
                <div className="col-md-3">
                  <label className="form-label">Moms (%)</label>
                  <select
                    className="form-select"
                    value={newItemData.tax_rate?.replace('%', '') || defaultVatRate}
                    onChange={(e) => handleNewItemFieldChange('tax_rate', `${e.target.value}%`)}
                  >
                    <option value="0">0</option>
                    <option value="6">6</option>
                    <option value="12">12</option>
                    <option value="25">25</option>
                  </select>
                </div>
              )}
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">Artikeltyp</label>
                <select
                  className="form-select"
                  value={newItemData.type}
                  onChange={(e) => handleNewItemFieldChange('type', e.target.value)}
                >
                  <option value="Service">Tjänst</option>
                  <option value="Goods">Varor</option>
                </select>
              </div>
              <div className="col-md-6 d-flex align-items-end">
                <div className="text-end w-100">
                  <div className="fw-bold">
                    Total: {formatCurrency((newItemData.quantity || 1) * (newItemData.unitprice || 0))}
                  </div>
                  <small className="text-muted">
                    {newItemData.quantity || 1} × {formatCurrency(newItemData.unitprice || 0)}
                  </small>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleHideAddModal}>
              Avbryt
            </Button>
            <Button
              variant="primary"
              onClick={handleAddConfiguredItem}
              disabled={!newItemData.description?.trim() || newItemData.unitprice <= 0}
            >
              <i className="bi bi-plus me-2"></i>
              Lägg till artikel
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Summary section */}
        {items.length > 0 && (
          <div className="quote-summary mt-4">
            <div className="row justify-content-end">
              <div className="col-md-6">
                <div className="summary-card">
                  <div className="summary-row">
                    <span className="label">{isTaxable ? 'Delsumma (exkl. moms):' : 'Delsumma:'}</span>
                    <span className="value">{formatCurrency(calculations.subtotal)}</span>
                  </div>

                  {isTaxable && Object.entries(calculations.taxGroups).map(([rate, data]) => (
                    <div className="summary-row" key={rate}>
                      <span className="label">Moms ({rate}%):</span>
                      <span className="value">{formatCurrency(data.tax)}</span>
                    </div>
                  ))}

                  {roundTotal && Math.abs(calculations.roundingAmount) > 0.001 && (
                    <div className="summary-row">
                      <span className="label">Avrundning:</span>
                      <span className="value">{formatCurrency(calculations.roundingAmount)}</span>
                    </div>
                  )}

                  <div className="summary-row total">
                    <span className="label">{isTaxable ? 'Totalt (inkl. moms):' : 'Totalt:'}</span>
                    <span className="value">{formatCurrency(calculations.finalTotal)}</span>
                  </div>

                  <div className="summary-row mt-3">
                    <Form.Check
                      type="switch"
                      id="rounding-switch"
                      label="Avrundning"
                      checked={roundTotal}
                      onChange={(e) => handleRoundingToggle(e.target.checked)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default InvoiceItemListBootstrap;
